import Lead from "../../../model/lead/Lead";
import {Action} from "redux-actions";
import {
	LEAD_AGENT_UPDATED,
	LEAD_CREATED,
	LEAD_EDITED,
	LEAD_EVENTS_FETCHED,
	LEAD_FETCHED,
	LEAD_STATUS_UPDATED,
	LEAD_WATCHERS_UPDATED
} from "./actionType";
import LeadData from "../../../model/lead/LeadData";
import {OnFailure, OnSuccess, OnSuccessData} from "../actionHelpers";
import {Dispatch} from "redux";
import {api} from "../../../api/api";
import {Option} from "ts-option";
import {UpdateLeadAssigneesApiDto, UpdateLeadStatusApiDto} from "../../../api/model/rest";
import LeadEvent, {LeadEventType} from "../../../model/lead/event/LeadEvent";
import LeadStatusEvent from "../../../model/lead/event/LeadStatusEvent";
import {fetchSite} from "../site/site";
import {fetchIndividualContact} from "../contacts/contacts";
import Tag from "../../../model/tag/Tag";
import {TAGS_SET_TO_LEAD} from "../tag/actionType";
import JsonConverter from "../../../api/helper/JsonConverter";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import {noop} from "react-select/lib/utils";

/**
 * Lead creation successful action.
 * @param {Lead} lead Created site.
 * @returns {Action<LEAD_CREATED>}
 */
export function leadCreated(lead: Lead): Action<LEAD_CREATED> {
	return {type: LEAD_CREATED, payload: {lead}};
}

/**
 * Lead fetched successful action.
 * @param {Lead} lead fetched lead.
 * @return {Action<LEAD_FETCHED>}
 */
export function leadFetched(lead: Lead): Action<LEAD_FETCHED> {
	return {type: LEAD_FETCHED, payload: {lead}}
}

/**
 * Lead edition successful action.
 * @param {Lead} lead Edited lead.
 * @returns {Action<LEAD_EDITED>}
 */
export function leadEdited(lead: Lead): Action<LEAD_EDITED> {
	return {type: LEAD_EDITED, payload: {lead}};
}

/**
 * Lead status update successful action.
 * @param {string} leadId Lead identifier.
 * @param {string} statusId updated status id
 * @returns {Action<LEAD_STATUS_UPDATED>}
 */
export function leadStatusUpdated(leadId: string, statusId: string): Action<LEAD_STATUS_UPDATED> {
	return {type: LEAD_STATUS_UPDATED, payload: {leadId, statusId}};
}

/**
 * Lead events fetched successful action.
 * @param {string} leadId lead identifier.
 * @param {Array<LeadEvent | LeadSoftCheckEvent | LeadStatusEvent>} events lead events.
 * @return {Action<LEAD_EVENTS_FETCHED>}
 */
export function leadEventsFetched(leadId: string,
	events: Array<LeadEvent | LeadStatusEvent>): Action<LEAD_EVENTS_FETCHED> {
	return {type: LEAD_EVENTS_FETCHED, payload: {leadId, events: events}};
}

/**
 * Lead watchers updated successful action
 * @param {string} leadId
 * @param {string[]} watchers
 * @returns {{type: string | LEAD_WATCHERS_UPDATED; payload: {leadId: string; watchers: string[]}}}
 */
export function leadWatchersUpdated(leadId: string, watchers: string[]) {
	return {type: LEAD_WATCHERS_UPDATED, payload: {leadId, watchers}}
}

/**
 * Agent updated action.
 * @param {string} leadId Id of the lead.
 * @param {Option<string>} agentId id of the now current agent.
 */
export function leadAgentUpdated(leadId: string, agentId: Option<string>) {
	return {type: LEAD_AGENT_UPDATED, payload: {leadId, agentId}}
}

/**
 * Lead tag added successful action
 * @param {string} leadId
 * @param {Tag} tag
 * @returns {{type: string | TAGS_SET_TO_LEAD; payload: {leadId: string; tag: Tag}}}
 */
export function tagsSeted(leadId: string, tags: Tag[]) {
	return {type: TAGS_SET_TO_LEAD, payload: {leadId, tags}}
}

/**
 * Create lead action.
 */
export function createLead(lead: LeadData, onSuccess: OnSuccessData<Lead>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadApi.createLead(lead).then((lead: Lead) => {
			dispatch(leadCreated(lead));
			dispatch(fetchIndividualContact(lead.data.principalContactId, noop, noop))
			onSuccess(lead);
		}).catch(onFailure);

	}
}

/**
 * Create lead action.
 */
export function editLead(lead: Lead, onSuccess: OnSuccessData<Lead>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadApi.updateLead(lead.id, lead.data).then(updatedLead => {
			onSuccess(updatedLead);
			dispatch(leadEdited(updatedLead))
		}).catch(onFailure);
	}
}

/**
 * Update lead status.
 * @param {string} leadId Lead id to be updated.
 * @param {string} statusId New lead status id.
 * @param {OnSuccess} onSuccess Success callback.
 * @param {OnFailure} onFailure Failure callback.
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function updateLeadStatus(leadId: string, statusId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	const data: UpdateLeadStatusApiDto = {statusId};
	return function(dispatch: Dispatch<any>) {
		api.instance.leadApi.updateStatus(leadId, data).then(() => {
			onSuccess();
			dispatch(leadStatusUpdated(leadId, statusId));
			dispatch(fetchLead(leadId, () => {}, () => {}));
		}).catch(onFailure)
	}
}

/**
 * Fetch lead events action.
 * @param {string} leadId lead identifier.
 * @param {OnSuccessData<LeadEventType[]>} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchLeadEvents(leadId: string, onSuccess: OnSuccessData<LeadEventType[]>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadApi.fetchLeadEvents(leadId).then(
			(events: Array<LeadEventType>) => {
				dispatch(leadEventsFetched(leadId, events));
				onSuccess(events);
			}).catch(onFailure);
	}
}

/**
 * Fetch single lead and it dependencies.
 * @param {string} leadId lead id to fetch.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 */
export function fetchLead(leadId: string, onSuccess: OnSuccessData<Lead>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadApi.fetchLead(leadId).then(lead => {
			lead.data.siteId.map(
				sId => dispatch(fetchSite(sId, () => {}, error => notifyBugsnagAndShowToast(error, "Fetch Site"))));
			dispatch(fetchIndividualContact(lead.data.principalContactId, () => {},
				error => notifyBugsnagAndShowToast(error, "Fetch Contacts")));
			dispatch(leadFetched(lead));
			onSuccess(lead);
		}).catch(onFailure);
	}
}

/**
 * Fetch single lead and it dependencies by reference number
 * @param {string} refId lead id to fetch.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 */
export function fetchLeadByRefNo(refId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadApi.fetchLeadByRefNo(refId).then(lead => {
			lead.data.siteId.map(
				sId => dispatch(fetchSite(sId, () => {}, error => notifyBugsnagAndShowToast(error, "Fetch Site"))));
			dispatch(fetchIndividualContact(lead.data.principalContactId, () => {},
				error => notifyBugsnagAndShowToast(error, "Fetch Contact")));
			dispatch(leadFetched(lead));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Update lead watchers action.
 * @param {string} leadId lead identifier.
 * @param {string[]} watchers
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function updateLeadWatchers(leadId: string, watchers: string[], onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadApi.updateLeadWatchers(leadId, watchers).then(() => {
				dispatch(leadWatchersUpdated(leadId, watchers));
				onSuccess();
			}
		).catch(onFailure)
	}
}

/**
 * Update lead assignees action
 * @param {string} leadId lead identifier.
 * @param {string} agentId lead agent identifier.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function updateLeadAssignees(leadId: string,
	agentId: Option<string>,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		const data: UpdateLeadAssigneesApiDto = JsonConverter.removeEmptyFields({
			agentId: agentId.orNull,
			assignees: []
		});
		api.instance.leadApi.updateLeadAssignees(leadId, data).then(() => {
				dispatch(leadAgentUpdated(leadId, agentId));
				onSuccess();
			}
		).catch(onFailure)
	}
}

/**
 * Attach to lead uploaded files
 * @param {string} leadId
 * @param attachmentIds
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function attachUploadedFiles(leadId: string, attachmentIds: string[], onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadApi.attachUploadedFiles(leadId, attachmentIds).then(() => {
			dispatch(fetchLeadEvents(leadId, () => {}, onFailure));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Set tags to lead
 * @param {string} leadId
 * @param {Tag[]} tags
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function setTags(leadId: string, tags: Tag[], onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadApi.setTags(leadId, tags.map(t => t.name)).then(() => {
			dispatch(tagsSeted(leadId, tags));
			onSuccess();
		}).catch(onFailure);
	}
}

export function sendSmsToLeadContact(leadId: string,
	message: string,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadApi.sendSmsToLeadContact(leadId, message).then(() => {
			dispatch(fetchLead(leadId, () => {}, () => {}));
			dispatch(fetchLeadEvents(leadId, () => {}, onFailure));
			onSuccess();
		}).catch(onFailure);
	}
}

export function sendEmailToLeadContact(leadId: string,
	subject: string,
	message: string,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadApi.sendEmailToLeadContact(leadId, subject, message).then(() => {
			dispatch(fetchLeadEvents(leadId, () => {}, onFailure));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Accept quote
 * @param {string} leadId
 * @param {string} quoteId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function acceptQuote(leadId: string, quoteId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadApi.acceptQuote(quoteId).then((jobId) => {
			dispatch(fetchLeadEvents(leadId, () => {}, onFailure));
			dispatch(fetchLead(leadId, () => {}, () => {}));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Add call event
 * @param {string} leadId
 * @param {string} message
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function addCallEvent(leadId: string, message: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadApi.addCallEvent(leadId, message).then(() => {
			dispatch(fetchLeadEvents(leadId, () => {}, onFailure));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Restore quote
 * @param {string} leadId
 * @param {string} quoteId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function restoreQuote(leadId: string, quoteId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadApi.restoreQuote(quoteId).then(() => {
			dispatch(fetchLeadEvents(leadId, () => {}, onFailure));
			onSuccess();
		}).catch(onFailure);
	}
}
